'use client';

import { cloneElement, type ReactElement } from 'react';

import type { ClickContext } from '../../core/constants';
import type { ClickMetadata } from '../../core/types';
import { usePersonalizationTracker } from '../PersonalizationProvider';

type ChildProps = Record<string, unknown>;

/**
 * Props for TrackClick component.
 *
 * @property {string} productId - Unique product identifier
 * @property {ClickContext} context - Context where the click occurs
 * @property {ClickMetadata} [metadata] - Optional click metadata
 * @property {ReactElement<ChildProps>} children - Single child element to track clicks on
 */
interface TrackClickProps {
  productId: string;
  context: ClickContext;
  metadata?: ClickMetadata;
  children: ReactElement<ChildProps>;
}

/**
 * Component that wraps a child element and tracks click events.
 * Intercepts onClick handler to track the click before executing original handler.
 *
 * Note: Only accepts a single React element child.
 *
 * @param {TrackClickProps} props - Component props
 *
 * @example
 * // Basic usage
 * <TrackClick productId="prod_123" context="product_card">
 *   <button onClick={handleClick}>View Product</button>
 * </TrackClick>
 *
 * @example
 * // With metadata
 * <TrackClick
 *   productId="prod_123"
 *   context="recommendation_section"
 *   metadata={{
 *     position: 2,
 *     sectionName: 'You May Also Like'
 *   }}
 * >
 *   <ProductCard />
 * </TrackClick>
 */
export function TrackClick({ productId, context, metadata, children }: TrackClickProps) {
  const tracker = usePersonalizationTracker();

  return cloneElement(children, {
    onClick: (...args: unknown[]) => {
      tracker.click(productId, context, metadata);

      const originalHandler = children.props['onClick'];

      if (typeof originalHandler === 'function') {
        return (originalHandler as (...a: unknown[]) => unknown)(...args);
      }
    },
  });
}
